import AppModal from '@/components/AppModal/AppModal';
import { config } from '@/config';
import { SETTING_TYPE, timeFormatEnum } from '@/constants/enum';
import { time24h } from '@/helpers';
import { useCreateDraftOrderMutation, useGetLocationsQuery } from '@/redux/query';
import { time24hTo12h } from '@/utils';
import { Banner, BlockStack, Checkbox, DatePicker, InlineGrid, Select } from '@shopify/polaris';
import { format, parse } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';
import { DatePickerStyled, LinkStyled } from '../../../styled';

interface Props {
  open: boolean;
  onCancel: () => void;
  dateFormat: string;
  timeFormat: string;
}

export const DraftOrderModal = ({ open, onCancel, timeFormat, dateFormat }: Props) => {
  const today = new Date();
  const [{ month, year }, setDate] = useState({ month: today.getMonth(), year: today.getFullYear() });
  const { data: locationData } = useGetLocationsQuery({ shop: config.shop });
  const [bannerState, setBannerState] = useState({
    isSaving: false,
    isOpen: false,
    draftOrderId: '',
  });
  const [createDraftOrder, { isLoading: isLoadingCreateDraftOrder }] = useCreateDraftOrderMutation();

  const [selectedDates, setSelectedDates] = useState({
    start: today,
    end: today,
  });

  const locationOptions = useMemo(() => {
    return (
      locationData?.data?.map((location) => ({
        label: location.name,
        value: location.id.toString(),
      })) || []
    );
  }, [locationData?.data]);

  const [draftOrderState, setDraftOrderState] = useState({
    method: '',
    locationId: '',
    isAddTimeSlot: false,
    startTime: '',
    endTime: '',
  });

  const [errors, setErrors] = useState({
    startTimeError: '',
    endTimeError: '',
  });

  const timeStarts = useMemo(() => {
    if (draftOrderState.endTime) {
      const endIndex = time24h.findIndex((item) => item.value === draftOrderState.endTime);
      return time24h.slice(0, endIndex).map((item) => ({
        key: item.value,
        label: timeFormat === timeFormatEnum.TWELVE_HOURS_FORMAT ? time24hTo12h(item.value) : item.value,
        value: item.value,
      }));
    } else {
      return time24h.map((item) => ({
        key: item.value,
        label: timeFormat === timeFormatEnum.TWELVE_HOURS_FORMAT ? time24hTo12h(item.value) : item.value,
        value: item.value,
      }));
    }
  }, [draftOrderState.endTime, timeFormat]);

  const timeEnds = useMemo(() => {
    if (draftOrderState.startTime) {
      const startIndex = time24h.findIndex((item) => item.value === draftOrderState.startTime);
      return time24h.slice(startIndex).map((item) => ({
        key: item.value,
        label: timeFormat === timeFormatEnum.TWELVE_HOURS_FORMAT ? time24hTo12h(item.value) : item.value,
        value: item.value,
      }));
    } else {
      return time24h.map((item) => ({
        key: item.value,
        label: timeFormat === timeFormatEnum.TWELVE_HOURS_FORMAT ? time24hTo12h(item.value) : item.value,
        value: item.value,
      }));
    }
  }, [draftOrderState.startTime, timeFormat]);

  const methodOptions = useMemo(
    () => [
      {
        value: SETTING_TYPE.SHIPPING,
        label: 'Shipping',
      },
      {
        value: SETTING_TYPE.DELIVERY,
        label: 'Local delivery',
      },
      {
        value: SETTING_TYPE.PICKUP,
        label: 'Pickup',
      },
    ],
    [],
  );

  const handleMonthChange = useCallback((month: number, year: number) => setDate({ month, year }), []);

  const handleSubmit = () => {
    setBannerState((prev) => ({ ...prev, isOpen: false, isSaving: true }));

    if (draftOrderState.isAddTimeSlot) {
      if (!draftOrderState.startTime || !draftOrderState.endTime) {
        setErrors({
          startTimeError: !draftOrderState.startTime ? 'Please select start time' : '',
          endTimeError: !draftOrderState.endTime ? 'Please select end time' : '',
        });
        return;
      }
    }

    if (!draftOrderState.method || (draftOrderState.method !== SETTING_TYPE.SHIPPING && !draftOrderState.locationId)) {
      return;
    }

    createDraftOrder({
      shop: config.shop,
      method: draftOrderState.method,
      ...(draftOrderState.locationId && { locationId: Number(draftOrderState.locationId) }),
      pickupDate: format(selectedDates.start, dateFormat),
      pickupTime: draftOrderState.isAddTimeSlot
        ? `${format(parse(draftOrderState.startTime, 'HH:mm', new Date()), timeFormat)} - ${format(
            parse(draftOrderState.endTime, 'HH:mm', new Date()),
            timeFormat,
          )}`
        : '',
    })
      .unwrap()
      .then((res) => {
        setBannerState((prev) => ({
          ...prev,
          draftOrderId: res.data.draftOrderId?.split('/').pop() || '',
        }));
        setBannerState((prev) => ({ ...prev, isOpen: true, isSaving: false }));
      });
  };

  return (
    <AppModal
      title="Create draft"
      open={open}
      onSubmit={handleSubmit}
      onCancel={() => {
        setDraftOrderState((prev) => ({
          ...prev,
          method: '',
          locationId: '',
          isAddTimeSlot: false,
          startTime: '',
          endTime: '',
        }));
        setBannerState((prev) => ({ ...prev, isOpen: false, isSaving: false }));
        setErrors({ startTimeError: '', endTimeError: '' });
        onCancel();
      }}
      submitText="Create"
      cancelText="Cancel"
      disabledPrimary={false}
      loading={isLoadingCreateDraftOrder}
    >
      <BlockStack gap={'400'}>
        {bannerState.isOpen && (
          <Banner tone="success" onDismiss={() => setBannerState((prev) => ({ ...prev, isOpen: false }))}>
            A test order is created.{' '}
            <LinkStyled
              removeUnderline={false}
              onClick={(e) => {
                e.stopPropagation();
                window.open(
                  `https://admin.shopify.com/store/${config.storeName}/draft_orders/${bannerState.draftOrderId}`,
                  '_blank',
                );
              }}
            >
              View orders
            </LinkStyled>
          </Banner>
        )}
        <Select
          label={'Select a method'}
          placeholder={'Select a method'}
          value={draftOrderState.method}
          options={methodOptions || []}
          onChange={(value: SETTING_TYPE) => setDraftOrderState((prev) => ({ ...prev, method: value }))}
          error={bannerState.isSaving && !draftOrderState.method && 'Please select a method'}
        />
        {draftOrderState.method !== SETTING_TYPE.SHIPPING && (
          <Select
            label={'Select a location'}
            placeholder={'Select a location'}
            options={locationOptions || []}
            value={draftOrderState.locationId.toString()}
            onChange={(value) => setDraftOrderState((prev) => ({ ...prev, locationId: value }))}
            error={
              bannerState.isSaving &&
              draftOrderState.method !== SETTING_TYPE.SHIPPING &&
              !draftOrderState.locationId &&
              'Please select a location'
            }
          />
        )}
        <DatePickerStyled>
          <DatePicker
            month={month}
            year={year}
            onChange={setSelectedDates}
            onMonthChange={handleMonthChange}
            selected={selectedDates}
            disableDatesBefore={new Date(today.setHours(0, 0, 0, 0))}
          />
        </DatePickerStyled>
        <Checkbox
          label="Add time slot"
          checked={draftOrderState.isAddTimeSlot}
          onChange={() => {
            setDraftOrderState((prev) => ({ ...prev, isAddTimeSlot: !prev.isAddTimeSlot }));
            setErrors({ startTimeError: '', endTimeError: '' });
          }}
        />
        {draftOrderState.isAddTimeSlot && (
          <InlineGrid gap="400" columns={2}>
            <Select
              label="Select start time"
              placeholder="Select time"
              options={timeStarts}
              onChange={(value) => setDraftOrderState((prev) => ({ ...prev, startTime: value }))}
              value={draftOrderState.startTime}
              error={errors.startTimeError}
            />
            <Select
              label="Select end time"
              placeholder="Select time"
              options={timeEnds}
              onChange={(value) => setDraftOrderState((prev) => ({ ...prev, endTime: value }))}
              value={draftOrderState.endTime}
              error={errors.endTimeError}
            />
          </InlineGrid>
        )}
      </BlockStack>
    </AppModal>
  );
};
